import { useEffect, useState } from "react";
import { api } from "../services/api";
import ConfirmModal from "../components/ConfirmModal";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import type { Provider, StoreItem, Wish, Redemption, Kid, ProviderType, Completion } from "@starquest/core";
import styles from "./ParentHome.module.css";
import own from "./Store.module.css";

const PROVIDER_TYPES: { value: ProviderType; label: string; emoji: string }[] = [
  { value: "amazon",  label: "Amazon",  emoji: "🛒" },
  { value: "walmart", label: "Walmart", emoji: "🏪" },
  { value: "target",  label: "Target",  emoji: "🎯" },
  { value: "costco",  label: "Costco",  emoji: "🏬" },
  { value: "custom",  label: "Custom",  emoji: "✨" },
];

type Tab = "catalog" | "orders" | "wishes";

export default function Store() {
  const { familyId } = useAuthStore();
  const {
    providers, storeItems, wishes, redemptions, completions, kids,
    setProviders, setStoreItems, setWishes, setRedemptions, setCompletions, setKids,
  } = useFamilyStore();

  const [tab, setTab] = useState<Tab>("catalog");
  const [confirmDeleteProvider, setConfirmDeleteProvider] = useState<string | null>(null);
  const [confirmDeleteItem,     setConfirmDeleteItem]     = useState<string | null>(null);
  const [providerModal, setProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Partial<Provider> | null>(null);
  const [pName, setPName] = useState("");
  const [pType, setPType] = useState<ProviderType>("custom");
  const [pEmoji, setPEmoji] = useState("✨");

  const [itemModal, setItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<StoreItem> | null>(null);
  const [wishToConvert, setWishToConvert] = useState<Wish | null>(null);
  const [iProviderId, setIProviderId] = useState("");
  const [iName, setIName] = useState("");
  const [iEmoji, setIEmoji] = useState("🎁");
  const [iDesc, setIDesc] = useState("");
  const [iUrl, setIUrl] = useState("");
  const [iCost, setICost] = useState(50);
  const [iAvailable, setIAvailable] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!familyId) return;
    Promise.all([
      api.listProviders(familyId).then((p) => setProviders(p as Provider[])),
      api.listItems(familyId).then((i) => setStoreItems(i as StoreItem[])),
      api.listWishes(familyId).then((w) => setWishes(w as Wish[])),
      api.listRedemptions(familyId).then((r) => setRedemptions(r as Redemption[])),
      api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[])),
      api.listKids(familyId).then((k) => setKids(k as Kid[])),
    ]).catch(() => setError("Couldn't load store data. Make sure the API server is running (make local-api) and refresh."));
  }, [familyId]);

  const kidName = (kidId: string) => kids.find((k) => k.kidId === kidId)?.name ?? "Explorer";

  function openNewProvider() {
    setEditingProvider(null); setPName(""); setPType("custom"); setPEmoji("✨");
    setProviderModal(true);
  }
  function openEditProvider(p: Provider) {
    setEditingProvider(p); setPName(p.name); setPType(p.type); setPEmoji(p.emoji);
    setProviderModal(true);
  }
  async function saveProvider() {
    if (!familyId || !pName.trim()) return;
    setSaving(true); setError("");
    try {
      const payload = editingProvider
        ? { ...editingProvider, name: pName.trim(), type: pType, emoji: pEmoji }
        : { name: pName.trim(), type: pType, emoji: pEmoji, sortOrder: providers.length };
      await api.saveProviders(familyId, [payload]);
      setProviders(await api.listProviders(familyId) as Provider[]);
      setProviderModal(false);
    } catch { setError("Failed to save provider"); }
    finally { setSaving(false); }
  }
  async function deleteProvider(providerId: string) {
    if (!familyId) return;
    setConfirmDeleteProvider(providerId);
  }

  async function confirmDeleteProviderAction() {
    if (!familyId || !confirmDeleteProvider) return;
    const id = confirmDeleteProvider;
    setConfirmDeleteProvider(null);
    try {
      await api.deleteProvider(familyId, id);
      setProviders(await api.listProviders(familyId) as Provider[]);
      setStoreItems(await api.listItems(familyId) as StoreItem[]);
    } catch { setError("Failed to delete provider"); }
  }

  function openNewItem(providerId?: string) {
    setEditingItem(null); setWishToConvert(null);
    setIProviderId(providerId ?? providers[0]?.providerId ?? "");
    setIName(""); setIEmoji("🎁"); setIDesc(""); setIUrl(""); setICost(50); setIAvailable(true);
    setItemModal(true);
  }
  function openEditItem(item: StoreItem) {
    setEditingItem(item); setWishToConvert(null);
    setIProviderId(item.providerId); setIName(item.name); setIEmoji(item.emoji);
    setIDesc(item.description); setIUrl(item.externalUrl); setICost(item.pointCost); setIAvailable(item.isAvailable);
    setItemModal(true);
  }
  function openAddToStore(w: Wish) {
    setWishToConvert(w); setEditingItem(null);
    setIProviderId(providers[0]?.providerId ?? "");
    setIName(w.name); setIEmoji("🌟"); setIDesc(""); setIUrl(w.url ?? ""); setICost(50); setIAvailable(true);
    setItemModal(true);
  }
  function closeItemModal() { setItemModal(false); setWishToConvert(null); setError(""); }

  async function saveItem() {
    if (!familyId || !iName.trim() || !iProviderId || iCost < 1) return;
    setSaving(true); setError("");
    try {
      const itemId = editingItem?.itemId ?? crypto.randomUUID();
      await api.saveItem(familyId, itemId, {
        providerId: iProviderId, name: iName.trim(), emoji: iEmoji,
        description: iDesc.trim(), externalUrl: iUrl.trim(), pointCost: iCost, isAvailable: iAvailable,
        ...(editingItem ? { createdAt: editingItem.createdAt } : {}),
      });
      setStoreItems(await api.listItems(familyId) as StoreItem[]);
      closeItemModal();
    } catch { setError("Failed to save item"); }
    finally { setSaving(false); }
  }
  async function deleteItem(itemId: string) {
    if (!familyId) return;
    setConfirmDeleteItem(itemId);
  }

  async function confirmDeleteItemAction() {
    if (!familyId || !confirmDeleteItem) return;
    const id = confirmDeleteItem;
    setConfirmDeleteItem(null);
    try {
      await api.deleteItem(familyId, id);
      setStoreItems(storeItems.filter((i) => i.itemId !== id));
    } catch { setError("Failed to delete item"); }
  }

  async function approveRedemption(r: Redemption) {
    if (!familyId) return;
    try {
      await api.reviewRedemption(familyId, r.redemptionId, { action: "approve" });
      setRedemptions(redemptions.map((x) => x.redemptionId === r.redemptionId ? { ...x, status: "approved" as const } : x));
    } catch { setError("Failed to approve order"); }
  }
  async function declineRedemption(r: Redemption) {
    if (!familyId) return;
    try {
      await api.reviewRedemption(familyId, r.redemptionId, { action: "decline" });
      setRedemptions(redemptions.map((x) => x.redemptionId === r.redemptionId ? { ...x, status: "declined" as const } : x));
    } catch { setError("Failed to decline order"); }
  }

  async function declineWish(w: Wish) {
    if (!familyId) return;
    try {
      await api.reviewWish(familyId, w.wishId, { action: "decline" });
      setWishes(wishes.map((x) => x.wishId === w.wishId ? { ...x, status: "declined" as const } : x));
    } catch { setError("Failed to decline wish"); }
  }
  async function approveWishViaItem() {
    if (!familyId || !wishToConvert || !iName.trim() || !iProviderId || iCost < 1) return;
    setSaving(true); setError("");
    try {
      await api.reviewWish(familyId, wishToConvert.wishId, {
        action: "approve", providerId: iProviderId, pointCost: iCost, emoji: iEmoji, description: iDesc.trim(),
      });
      const [updatedWishes, updatedItems] = await Promise.all([
        api.listWishes(familyId), api.listItems(familyId),
      ]);
      setWishes(updatedWishes as Wish[]);
      setStoreItems(updatedItems as StoreItem[]);
      closeItemModal();
    } catch { setError("Failed to add wish to store"); }
    finally { setSaving(false); }
  }

  const pendingOrders   = redemptions.filter((r) => r.status === "pending");
  const fulfilledOrders = redemptions.filter((r) => r.status !== "pending");  // ADD THIS
  const pendingWishes   = wishes.filter((w) => w.status === "pending");

  return (
    <div className={styles.page}>
      <ParentSidebar pendingCount={completions.length} />
      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>🏪 Reward Store</h1>
            <div className={styles.sub}>Set up rewards your explorers can spend their stars on</div>
            <BottomNav />
    </div>
          <BottomNav />
    </div>

        {error && !providerModal && !itemModal && (
          <div style={{
            background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.3)",
            borderRadius: 12, padding: "10px 16px", fontSize: 13, color: "#ff8080",
            marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span>⚠️ {error}</span>
            <button type="button" onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ff8080", cursor: "pointer", fontSize: 16 }}>✕</button>
            <BottomNav />
    </div>
        )}

        <div className={own.tabs}>
          {(["catalog", "orders", "wishes"] as Tab[]).map((t) => (
            <button type="button" key={t} className={`${own.tab} ${tab === t ? own.tabActive : ""}`} onClick={() => setTab(t)}>
              {t === "catalog" ? "Catalog"
                : t === "orders" ? `Orders${pendingOrders.length > 0 ? ` (${pendingOrders.length})` : ""}`
                : `Wishes${pendingWishes.length > 0 ? ` (${pendingWishes.length})` : ""}`}
            </button>
          ))}
          <BottomNav />
    </div>

        {tab === "catalog" && (
          <div className={own.catalogGrid}>
            {providers.map((p) => {
              const providerItems = storeItems.filter((i) => i.providerId === p.providerId);
              return (
                <div key={p.providerId} className={own.providerCard}>
                  <div className={own.providerHead}>
                    <span className={own.providerEmoji}>{p.emoji}</span>
                    <div className={own.providerInfo}>
                      <div className={own.providerName}>{p.name}</div>
                      <div className={own.providerCount}>{providerItems.length} items</div>
                      <BottomNav />
    </div>
                    <div className={own.providerActions}>
                      <button type="button" className={own.iconBtn} onClick={() => openNewItem(p.providerId)}>+ Item</button>
                      <button type="button" className={own.iconBtn} onClick={() => openEditProvider(p)}>✏️</button>
                      <button type="button" className={`${own.iconBtn} ${own.danger}`} onClick={() => deleteProvider(p.providerId)}>🗑️</button>
                      <BottomNav />
    </div>
                    <BottomNav />
    </div>
                  {providerItems.map((item) => (
                    <div key={item.itemId} className={own.itemRow}>
                      <span className={own.itemEmoji}>{item.emoji}</span>
                      <div className={own.itemInfo}>
                        <div className={own.itemName}>{item.name}</div>
                        {item.externalUrl && (
                          <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" className={own.itemUrl}>🔗 Link</a>
                        )}
                        <BottomNav />
    </div>
                      <div className={own.itemCost}>⭐ {item.pointCost}</div>
                      {!item.isAvailable && <span className={own.hiddenBadge}>Hidden</span>}
                      <button type="button" className={own.iconBtn} onClick={() => openEditItem(item)}>✏️</button>
                      <button type="button" className={`${own.iconBtn} ${own.danger}`} onClick={() => deleteItem(item.itemId)}>🗑️</button>
                      <BottomNav />
    </div>
                  ))}
                  <BottomNav />
    </div>
              );
            })}
            <button type="button" className={own.addProviderCard} onClick={openNewProvider}>
              <span style={{ fontSize: 28 }}>+</span>
              <span>Add Provider</span>
            </button>
            <BottomNav />
    </div>
        )}

        {tab === "orders" && (
          <div>
            {pendingOrders.length === 0 && <div className={styles.empty}>No pending orders 🎉</div>}
            {pendingOrders.map((r) => (
              <div key={r.redemptionId} className={own.orderRow}>
                <span className={own.orderEmoji}>{r.itemEmoji}</span>
                <div className={own.orderInfo}>
                  <div className={own.orderName}>{r.itemName}</div>
                  <div className={own.orderMeta}>{kidName(r.kidId)} · ⭐ {r.pointCost} · {new Date(r.requestedAt).toLocaleDateString()}</div>
                  <BottomNav />
    </div>
                <div className={own.orderActions}>
                  <button type="button" className={`${styles.btn} ${styles.primary}`} onClick={() => approveRedemption(r)}>✓ Approve</button>
                  <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => declineRedemption(r)}>✕ Decline</button>
                  <BottomNav />
    </div>
                <BottomNav />
    </div>
            ))}
            {fulfilledOrders.length > 0 && (
              <div className={own.histSection}>
                <div className={own.histLabel}>Fulfilled</div>
                {fulfilledOrders.slice(0, 10).map((r) => (
                  <div key={r.redemptionId} className={`${own.orderRow} ${own.orderDone}`}>
                    <span className={own.orderEmoji}>{r.itemEmoji}</span>
                    <div className={own.orderInfo}>
                      <div className={own.orderName}>{r.itemName}</div>
                      <div className={own.orderMeta}>{kidName(r.kidId)} · {r.status.toUpperCase()}</div>
                      <BottomNav />
    </div>
                    <div className={own.statusChip}>{r.status === "approved" ? "✅" : "❌"}</div>
                    <BottomNav />
    </div>
                ))}
                <BottomNav />
    </div>
            )}
            <BottomNav />
    </div>
        )}

        {tab === "wishes" && (
          <div>
            {pendingWishes.length === 0 && <div className={styles.empty}>No pending wishes ✨</div>}
            {pendingWishes.map((w) => (
              <div key={w.wishId} className={own.wishRow}>
                <span className={own.wishIcon}>🌟</span>
                <div className={own.wishInfo}>
                  <div className={own.wishName}>{w.name}</div>
                  <div className={own.wishMeta}>
                    {kidName(w.kidId)} · {new Date(w.submittedAt).toLocaleDateString()}
                    {w.url && <> · <a href={w.url} target="_blank" rel="noopener noreferrer" className={own.wishUrl}>🔗 View item</a></>}
                    <BottomNav />
    </div>
                  <BottomNav />
    </div>
                <div className={own.wishActions}>
                  <button type="button" className={`${styles.btn} ${styles.primary}`} onClick={() => openAddToStore(w)}>Add to Store</button>
                  <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => declineWish(w)}>Decline</button>
                  <BottomNav />
    </div>
                <BottomNav />
    </div>
            ))}
            <BottomNav />
    </div>
        )}
      </main>

      {providerModal && (
        <div className={own.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) setProviderModal(false); }}>
          <div className={own.modal}>
            <h2 className={own.modalTitle}>{editingProvider ? "Edit Provider" : "Add Provider"}</h2>
            <div className={own.formRow}>
              <label className={own.label}>Type</label>
              <div className={own.typeRow}>
                {PROVIDER_TYPES.map((t) => (
                  <button type="button" key={t.value} className={`${own.typeBtn} ${pType === t.value ? own.typeBtnActive : ""}`}
                    onClick={() => { setPType(t.value); setPEmoji(t.emoji); }}>
                    {t.emoji} {t.label}
                  </button>
                ))}
                <BottomNav />
    </div>
              <BottomNav />
    </div>
            <div className={own.formRow}>
              <label className={own.label}>Name</label>
              <input className={own.input} value={pName} onChange={(e) => setPName(e.target.value)} placeholder="e.g. Amazon" />
              <BottomNav />
    </div>
            <div className={own.formRow}>
              <label className={own.label}>Emoji</label>
              <input className={own.input} value={pEmoji} onChange={(e) => setPEmoji(e.target.value)} style={{ width: 80 }} />
              <BottomNav />
    </div>
            {error && <p className={own.error}>{error}</p>}
            <div className={own.modalBtns}>
              <button type="button" className={`${styles.btn} ${styles.primary}`} disabled={saving || !pName.trim()} onClick={saveProvider}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => setProviderModal(false)}>Cancel</button>
              <BottomNav />
    </div>
            <BottomNav />
    </div>
          <BottomNav />
    </div>
      )}

      {itemModal && (
        <div className={own.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) closeItemModal(); }}>
          <div className={own.modal}>
            <h2 className={own.modalTitle}>
              {wishToConvert ? `Add "${wishToConvert.name}" to Store` : editingItem ? "Edit Item" : "Add Item"}
            </h2>
            <div className={own.formRow}>
              <label className={own.label}>Provider</label>
              <select className={own.input} value={iProviderId} onChange={(e) => setIProviderId(e.target.value)}>
                {providers.map((p) => <option key={p.providerId} value={p.providerId}>{p.emoji} {p.name}</option>)}
              </select>
              <BottomNav />
    </div>
            <div className={own.formRow}>
              <label className={own.label}>Name</label>
              <input className={own.input} value={iName} onChange={(e) => setIName(e.target.value)} placeholder="e.g. Pizza Night" />
              <BottomNav />
    </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div className={own.formRow} style={{ flex: 1 }}>
                <label className={own.label}>Emoji</label>
                <input className={own.input} value={iEmoji} onChange={(e) => setIEmoji(e.target.value)} style={{ width: 80 }} />
                <BottomNav />
    </div>
              <div className={own.formRow} style={{ flex: 1 }}>
                <label className={own.label}>Point Cost ⭐</label>
                <input className={own.input} type="number" min={1} value={iCost} onChange={(e) => setICost(Math.max(1, +e.target.value))} />
                <BottomNav />
    </div>
              <BottomNav />
    </div>
            <div className={own.formRow}>
              <label className={own.label}>Description (optional)</label>
              <input className={own.input} value={iDesc} onChange={(e) => setIDesc(e.target.value)} placeholder="What does the explorer get?" />
              <BottomNav />
    </div>
            <div className={own.formRow}>
              <label className={own.label}>Link (optional)</label>
              <input className={own.input} value={iUrl} onChange={(e) => setIUrl(e.target.value)} placeholder="https://amazon.com/..." />
              <BottomNav />
    </div>
            <div className={own.formRow}>
              <label className={own.label} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={iAvailable} onChange={(e) => setIAvailable(e.target.checked)} />
                Available to explorers
              </label>
              <BottomNav />
    </div>
            {error && <p className={own.error}>{error}</p>}
            <div className={own.modalBtns}>
              <button type="button" className={`${styles.btn} ${styles.primary}`}
                disabled={saving || !iName.trim() || !iProviderId || iCost < 1}
                onClick={wishToConvert ? approveWishViaItem : saveItem}>
                {saving ? "Saving…" : wishToConvert ? "Add to Store" : "Save"}
              </button>
              <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={closeItemModal}>Cancel</button>
              <BottomNav />
    </div>
            <BottomNav />
    </div>
          <BottomNav />
    </div>
      )}
      <BottomNav />
      {confirmDeleteProvider && (() => {
        const p = providers.find((x) => x.providerId === confirmDeleteProvider);
        const name = p?.name || "this provider";
        return (
          <ConfirmModal
            message={`Delete "${name}"?`}
            detail="All items in this provider will also be deleted. This cannot be undone."
            confirmLabel="Delete"
            typeToConfirm={name}
            onCancel={() => setConfirmDeleteProvider(null)}
            onConfirm={confirmDeleteProviderAction}
          />
        );
      })()}

      {confirmDeleteItem && (() => {
        const item = storeItems.find((x) => x.itemId === confirmDeleteItem);
        const name = item?.name || "this item";
        return (
          <ConfirmModal
            message={`Delete "${name}"?`}
            detail="Kids will no longer be able to see or request this item."
            confirmLabel="Delete"
            typeToConfirm={name}
            onCancel={() => setConfirmDeleteItem(null)}
            onConfirm={confirmDeleteItemAction}
          />
        );
      })()}
    </div>
  );
}
